import { useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { formatHumanDate } from '@/lib/format.js';
import { setEffectiveLock } from '@/services/deals.service.js';

// The lock key beside "eff" in the deal header (David 2026-10-27: "there should
// basically just be a lock key next to the eff date"). Locked, the client signs
// at the policy start as it stands, a past date included, and is not asked for
// one. The state is the server's (deal.effective_lock, bind-window.service
// effectiveLockForDeal); this derives nothing. A bind recorded through a broker
// door is already the broker's date, so that lock has nothing to undo here.
export default function EffectiveLockKey({ dealId, lock, policyStart, onChange }) {
  const [busy, setBusy] = useState(false);
  const start = formatHumanDate(policyStart);
  const base = 'inline-flex items-center gap-1 rounded px-1 py-px text-xs';
  if (!lock || lock.ok !== true) {
    return <span className={`${base} text-text-dim`} title="The lock could not be read"><Lock size={12} strokeWidth={1.5} />lock: could not check</span>;
  }
  if (lock.by_bind) {
    return <span className={`${base} text-text`} title={`Your bind holds ${start || 'the policy start'}`}><Lock size={12} strokeWidth={2} />locked by your bind</span>;
  }
  async function toggle() {
    setBusy(true);
    try {
      const res = await setEffectiveLock(dealId, !lock.flag);
      if (!res?.success) throw new Error(res?.error || 'failed');
      const now = res.lock;
      if (!now || now.ok !== true) toast.info('Saved, but the lock could not be read back. Reload to check it.');
      else toast.success(now.locked ? `Effective date locked${start ? ` at ${start}` : ''}` : 'Effective date unlocked');
      onChange?.();
    } catch (err) {
      toast.error(`Lock failed: ${err?.body?.error || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }
  const locked = lock.flag === true;
  const title = locked
    ? `Locked: the client signs at ${start || 'the policy start'}. Click to unlock.`
    : `Lock ${start || 'the policy start'} so the client signs at it, a past date included`;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={title}
      data-effective-lock={locked ? 'locked' : 'unlocked'}
      className={`${base} hover:bg-surface-strong disabled:opacity-50 ${locked ? 'font-medium text-text' : 'text-text-muted hover:text-text'}`}
    >
      {locked ? <Lock size={12} strokeWidth={2} /> : <LockOpen size={12} strokeWidth={1.5} />}
      {busy ? 'saving...' : locked ? 'locked' : 'lock'}
    </button>
  );
}
